import { Theme } from '@mui/material';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectExpandedModsDialog, selectIsEditMod, selectStandard } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ isExpanded?: boolean }>()((theme: Theme, { isExpanded }) => ({
  inputFields: {
    minWidth: isExpanded ? '264px' : '211px',
    '& .MuiFilledInput-input': {
      height: '2em',
    },
  },
}));

interface EffectiveEndDateProps {
  readOnly?: boolean;
}

export const EffectiveEndDate = ({ readOnly }: EffectiveEndDateProps) => {
  const isExpanded = useSelector(selectExpandedModsDialog);
  const { classes } = useStyles({ isExpanded });
  const { t } = useTranslation();
  const { control, formState: { errors }, setError, clearErrors, setValue } = useFormContext();
  const value: Date | string | null | undefined = useWatch({ name: 'endDate' });
  const startDateWatch: Date | string | null | undefined = useWatch({ name: 'startDate' });
  const isModEdit = useSelector(selectIsEditMod);
  const standard = useSelector(selectStandard);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });

  const [isFieldRequired, setFieldRequired] = useState<boolean>(false);

  const handleChange = (param: Date | string | null | undefined) => {
    if (param) {
      setValue('endDate', param);
      setFieldRequired(false);
      clearErrors('endDate');
    } else {
      if (startDateWatch) {
        setValue('endDate', null);
        setFieldRequired(true);
        setError('endDate', { type: 'required', message: 'Required' });
      } else {
        setValue('endDate', null);
        setFieldRequired(false);
        clearErrors('endDate');
      }
    }
  };

  useEffect(() => {
    if (startDateWatch && value == null) {
      handleChange(value);
    } else {
      handleChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateWatch, value, isFieldRequired]);

  return (
    <Controller
      control={control}
      name={'endDate'}
      rules={{
        required: isFieldRequired,
      }}
      render={({ field: { onChange, value, ...rest } }) => {
        const endDate = moment(value ?? null);
        return (
          <React.Fragment>
            <DateRangePicker
              onChange={newValue => {
                if (newValue.startDate !== undefined) {
                  const newDate = moment(newValue.startDate);
                  const setTo = newDate.isValid() ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : null;
                  onChange(setTo);
                  handleChange(setTo);
                } else {
                  onChange(null);
                  handleChange(null);
                }
              }}
              value={value}
              minDate={startDateWatch ? new Date(startDateWatch) : null}
              required={isFieldRequired}
              quickSelect={false}
              onCancel={() => {}}
              clear
              open={false}
              variant="filled"
              single
              placeholder={t('placement.placementMods.effectiveEndDate')}
              useMaxWidth={true}
              showIcon={true}
              className={classes.inputFields}
              labelClass={classes.inputFields}
              actionBar={false}
              clickAway={true}
              trailingIconShow={true}
              readOnly={readOnly !== undefined ? readOnly : (standard ? true : !isModEdit)}
              isDisabled={isSaveModApiCalling}
              keepClickAwayVal={false}
              initialDateRange={{
                startDate: endDate.isValid() ? new Date(endDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                endDate: endDate.isValid() ? new Date(endDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                useLabelAsValue: true,
              }}
              {...rest}
              error={isFieldRequired}
              helperText={!isNullOrUndefined(errors?.endDate) ? (errors?.endDate?.message as string) || '' : undefined}
            />
          </React.Fragment>
        );
      }}
    />
  );
};
